// src/components/blog/ArticleCard.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ArticleMetadata } from '@dopomogai/types';
import { Calendar, Clock, User } from 'lucide-react';

interface ArticleCardProps {
  article: ArticleMetadata;
  index: number;
}

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

export const ArticleCard: React.FC<ArticleCardProps> = ({ article, index }) => {
  const { t } = useTranslation('blog');

  const formattedDate = new Date(article.publishedDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      custom={index}
      className="h-full"
    >
      <Link
        to={`/blog/${article.slug}`}
        className={clsx(
          "group block bg-white rounded-xl border-2 border-black overflow-hidden h-full",
          "flex flex-col transition-all duration-200 transform",
          "hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]",
          "active:translate-x-0 active:translate-y-0 active:shadow-none"
        )}
        aria-label={t('article.ariaLabel', { title: article.title })}
      >
        <div className="aspect-video overflow-hidden border-b-2 border-black">
          <img
            src={article.featuredImage}
            alt={`Featured image for ${article.title}`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="p-6 flex flex-col flex-grow">
          <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">
            {article.category}
          </p>
          <h3 className="mt-2 text-xl font-bold text-gray-900 leading-tight">
            {article.title}
          </h3>
          <p className="mt-2 text-slate-600 text-sm leading-relaxed flex-grow">
            {article.snippet}
          </p>
          <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="flex items-center gap-1.5">
              <User size={14} />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} />
              {formattedDate}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {t('article.readTime', { count: article.readTimeMinutes })}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};